import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Lightbulb, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { useArticlesStore } from '@/lib/stores/articlesStore'
import { Card, Badge, Spinner } from '@/components/ui'
import { formatDateTime } from '@/lib/utils'
import type { Proposal, ProposalStatus } from '@/types'

export function ProposalsPage() {
  const { proposals, isLoading, fetchProposals } = useArticlesStore()

  useEffect(() => {
    fetchProposals()
  }, [fetchProposals])

  const getStatusInfo = (status: ProposalStatus) => {
    switch (status) {
      case 'pending_inputs':
        return {
          label: 'Risposte richieste',
          variant: 'warning' as const,
          icon: AlertCircle,
        }
      case 'ready':
        return {
          label: 'Pronta',
          variant: 'info' as const,
          icon: Lightbulb,
        }
      case 'applied':
        return {
          label: 'Applicata',
          variant: 'success' as const,
          icon: CheckCircle,
        }
      case 'rejected':
        return {
          label: 'Scartata',
          variant: 'danger' as const,
          icon: XCircle,
        }
    }
  }

  if (isLoading && proposals.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Proposte</h1>
        <p className="text-sm text-gray-500 mt-1">
          {proposals.filter((p) => p.status !== 'applied' && p.status !== 'rejected').length} proposte in attesa
        </p>
      </div>

      {/* Proposals list */}
      {proposals.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessuna proposta ancora</p>
            <p className="text-sm text-gray-400 mt-1">
              Le proposte appariranno qui dopo l'analisi degli articoli
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {proposals.map((proposal: Proposal) => {
            const statusInfo = getStatusInfo(proposal.status)
            const StatusIcon = statusInfo.icon

            return (
              <Link key={proposal.id} to={`/proposals/${proposal.id}`}>
                <Card className="hover:border-primary-300 transition-colors">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <StatusIcon className="w-5 h-5 text-gray-400" />
                          <Badge variant={statusInfo.variant}>
                            {statusInfo.label}
                          </Badge>
                        </div>
                        <h3 className="font-medium text-gray-900 mb-1">
                          Proposta #{proposal.id.slice(0, 8)}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          {formatDateTime(proposal.created_at)}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {proposal.structural_analysis?.filter((s) => s.action === 'add').length || 0} elementi da aggiungere
                        </p>
                        {proposal.pending_inputs && proposal.pending_inputs.length > 0 && (
                          <p className="text-sm text-amber-600 mt-1">
                            {proposal.pending_inputs.filter((i) => !i.answer).length} risposte richieste
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
